import type { ApiClient } from 'grabbitbot-legacy-ui'
import type {
  AdminAuth0UnbindResult,
  AdminBotControlResult,
  AdminBotStatusV1,
  AdminBotStatusV3,
  AdminProduct,
  AdminProductModifyParams,
  AdminStrategy,
  AdminSubscription,
  AdminTransaction,
  AdminUserDetailed,
  Commission,
  CreditBalanceRow,
  CursorPage,
  GlobalProfitByUser,
  LifetimeProfit,
  LogEventsAnswer,
  ProcessLogRow,
  ProfitArchive,
  SatelitLogItem,
  StripeLogItem,
  UsageFee,
  UserProfitArchive,
  WalletRow,
} from './adminTypes'

/**
 * B6 domain client — the admin back-office API (`Controller\Admin\**` in the
 * legacy archive). See `legacy-migration/docs/analysis/
 * 2026-09-27-grabbitbot-b6-contract.md` (api.grabbitbot.com 0.6.0) for the
 * pinned shapes. EVERY route here requires the caller to hold the
 * `admin`/`is_admin` flag — the backend is the real authorization boundary
 * (`RequireAuth requireRole={ADMIN_ROLE}` in AppRoutes is UX only).
 *
 * `cache: false` everywhere: admin reads are look-up-a-specific-user tools,
 * never a dashboard the user revisits expecting a stale value, and several
 * of them (credit, bot status) must reflect a write that just happened on
 * the SAME page.
 *
 * BUSINESS-CRITICAL: product modify/cancel move real payment state; bot start/modify place/refuse a
 * real spotbot control request. None of these are retried by this client —
 * see each method's docblock for the specific contract rule it must not
 * violate.
 */
export function createAdminClient(client: ApiClient) {
  return {
    // --- users (#1-2) -------------------------------------------------------
    listUsers: () => client.get<string[]>('/admin/users', { cache: false }),
    listUsersDetailed: () => client.get<AdminUserDetailed[]>('/admin/users/detailed', { cache: false }),
    /** Clears a wrongly bound Auth0/Google link so the user's next login
     * links by verified e-mail again. `email` must be the stored address,
     * exactly. 404 `user_not_found` when no such user. Not retried. */
    unbindAuth0: (email: string) => client.post<AdminAuth0UnbindResult>('/admin/users/auth0/unbind', { email }),

    // --- payment (#3-8) ------------------------------------------------------
    /** #3 — every subscription, or one user's when `email` is given. */
    subscriptions: (email?: string) =>
      client.get<AdminSubscription[]>('/admin/payment/subscriptions', { query: { email }, cache: false }),
    /** #4 — every one-time product, or one user's. */
    products: (email?: string) =>
      client.get<AdminProduct[]>('/admin/payment/products', { query: { email }, cache: false }),
    /** #5 — BUSINESS-CRITICAL. `payed: false` DELETES the product (legacy,
     * kept) — the caller must confirm before sending that. Query params,
     * matching the contract's own pinned test
     * (`/admin/payment/products/{product}?email=&payed=1&payedAt=...`). */
    modifyProduct: (product: string, email: string, params: AdminProductModifyParams) =>
      client.patch<AdminProduct>(
        '/admin/payment/products/{product}',
        undefined,
        {
          params: { product },
          query: {
            email,
            payed: params.payed ? 1 : 0,
            alternativeMethod: params.alternativeMethod === undefined ? undefined : params.alternativeMethod ? 1 : 0,
            payedAt: params.payedAt || undefined,
          },
        },
      ),
    /** #6 — BUSINESS-CRITICAL, irreversible-looking (drops the payment
     * method, cancels every active subscription). Always answers 200 `[]`.
     * No `email` -> the contract says nothing happens; the page must not
     * let a click through without one. */
    cancelPayment: (email: string) =>
      client.patch<unknown[]>('/admin/payment/cancel', undefined, { query: { email } }),
    /** #7 — newest first. */
    transactions: (email: string) =>
      client.get<AdminTransaction[]>('/admin/payment/transactions', { query: { email }, cache: false }),
    /** #8 — 100/page; `cursor` is opaque, pass back verbatim. */
    stripeLogs: (email: string, cursor?: string) =>
      client.get<CursorPage<StripeLogItem>>('/admin/payment/stripe/logs', { query: { email, cursor }, cache: false }),

    // --- logs (#9-10) --------------------------------------------------------
    /** #9 — `pageSize` MUST be sent: legacy's `LIMIT ((int)$x) ?: 100`
     * quirk means an OMITTED `pageSize` answers `LIMIT 0` (zero rows), even
     * though the answer still echoes `pageSize: 100`. `date` must be one of
     * `last1Hour..last48Hours` or `manual` (with `from`/`to`); anything else
     * is a 200 `{error: "..."}` with no rows. */
    logEvents: (opts: {
      date: string
      pageSize: number
      page?: number
      order?: 'dateasc' | 'datedesc'
      email?: string
      hideAlg?: boolean
      from?: string
      to?: string
    }) =>
      client.get<LogEventsAnswer>('/admin/log/events', {
        query: {
          date: opts.date,
          pageSize: opts.pageSize,
          page: opts.page,
          order: opts.order,
          email: opts.email || undefined,
          hideAlg: opts.hideAlg ? 1 : undefined,
          from: opts.from,
          to: opts.to,
        },
        cache: false,
      }),
    /** #10 — same cursor convention as #8. */
    satelitLogs: (email: string, cursor?: string) =>
      client.get<CursorPage<SatelitLogItem>>('/admin/log/satelitApi', { query: { email, cursor }, cache: false }),

    // --- statistics archive (#11-12) -----------------------------------------
    /** #11 — ARCHIVE: the writer cron was disabled in legacy too, so this is
     * a 30-day zero-history feed in production as well as here. */
    profitArchive: () => client.get<ProfitArchive>('/admin/statistics/profit', { cache: false }),
    /** #12 — same feed for one user. */
    profitArchiveByUser: (email: string) =>
      client.get<UserProfitArchive>('/admin/statistics/profit/byUser', { query: { email }, cache: false }),

    // --- strategies (#13-16) --------------------------------------------------
    /** #13 — ARCHIVE, always `[]` (the `bc_` namespace has no writer). */
    strategies: () => client.get<AdminStrategy[]>('/admin/strategies', { cache: false }),
    /** #14 — ARCHIVE, always 410 `{id}` (unknown — no strategy is ever
     * written). Callers must treat 410 as "does not exist", not an error. */
    strategyView: (id: string) => client.get<AdminStrategy>('/admin/strategies/{id}', { params: { id }, cache: false }),
    /** #15 — ARCHIVE, always `[]`. */
    strategyStat: (id: string) =>
      client.get<unknown[]>('/admin/strategies/{id}/stat', { params: { id }, cache: false }),
    /** #16 — ARCHIVE: 409 `email_not_set` with no email, 409
     * `user_not_found` for an unknown one, else `[]`. Kept alongside the
     * real #29 monthly commission on `AdminUserStrategyCommissionPage` so
     * an admin sees both the (empty) frozen history and the live figure. */
    strategyCommissionArchive: (exchangeId: string, email: string) =>
      client.get<unknown[]>('/admin/strategies/{exchangeId}/commission', {
        params: { exchangeId },
        query: { email },
        cache: false,
      }),

    // --- credit (#17-20) -------------------------------------------------------
    /** #17 — every currency balance for the user (+ an unsaved zero
     * `credit` row if none exists yet). 410 `[]` for an unknown user, 409
     * `user_has_no_serviceId` for one with no service id. */
    creditAll: (email: string) => client.get<CreditBalanceRow[]>('/admin/credit/all', { query: { email }, cache: false }),
    /** #18 — one archived currency balance; a missing balance is shown as an
     * unsaved zero (read only since 2026-10-25, nothing is created). */
    creditInfo: (email: string, currency: string) =>
      client.get<CreditBalanceRow>('/admin/credit', { query: { email, currency }, cache: false }),

    // --- bot status / control (#21-24) ------------------------------------------
    /** #21 — legacy v1 shape; `statuses` is always `[]` on this route. */
    botStatusV1: (email: string) => client.get<AdminBotStatusV1>('/admin/bot/status', { query: { email }, cache: false }),
    /** #22 — every configured exchange for the user, incl. key PRESENCE
     * only (never the key/secret value itself). */
    botStatusV3: (email: string) => client.get<AdminBotStatusV3>('/v3/admin/bot/status', { query: { email }, cache: false }),
    /** #23 — BUSINESS-CRITICAL, ONE attempt (no 401 retry, unlike the
     * user's own B4 start). **Spotbot refusing answers 200 `[]`** — the
     * caller MUST treat an array/empty answer as "not started", never as
     * success. 409 `bot_action_in_progress` while any control request for
     * this user+exchange is in flight (admin or the user's own). 502
     * `spotbot_unreachable` leaves the outcome UNKNOWN — never auto-retry. */
    startBot: (exchangeId: string, email: string) =>
      client.post<AdminBotControlResult>('/v3/admin/bot/status/{exchangeId}', undefined, {
        params: { exchangeId },
        query: { email },
      }),
    /** #24 — BUSINESS-CRITICAL. Sends the WHOLE request body through to
     * spotbot verbatim (an `email` field in the body is forwarded too,
     * legacy quirk — the caller should not add one). Same `[]`-means-
     * refused rule as start. */
    modifyBot: (exchangeId: string, email: string, body: Record<string, unknown>) =>
      client.patch<AdminBotControlResult>('/v3/admin/bot/status/{exchangeId}', body, {
        params: { exchangeId },
        query: { email },
      }),

    // --- global profit by user (#25) --------------------------------------------
    /** #25 — every user's profit, grouped. `days` OR `month` (not both) —
     * pass exactly one. */
    globalProfitByUser: (
      exchangeId: string,
      resolution: 'daily' | 'hourly',
      coin2: string,
      opts: { days?: number; month?: string | number } = {},
    ) =>
      client.get<GlobalProfitByUser>('/v3/admin/spot/statistics/{exchangeId}/global-profit/byuser/{resolution}/{coin2}', {
        params: { exchangeId, resolution, coin2 },
        query: { days: opts.days, month: opts.month },
        cache: false,
      }),

    // --- lifetime profit (#26) --------------------------------------------------
    lifetimeProfit: (email: string) =>
      client.get<LifetimeProfit>('/v3/admin/spot/statistics/profit/lifetime', { query: { email }, cache: false }),

    // --- process logs (#27-28) ---------------------------------------------------
    /** #27/#28 — `date` is `YYYYMMDD`; legacy quirk (kept, but avoidable by
     * always sending an explicit date): an EMPTY date means "today WITH
     * dashes", which matches no table and answers `[]`. Until spotbot
     * provides the view this answers **503** `process_log_not_available` —
     * the caller must show that as "not available yet", never as an empty
     * list (an empty list would read as "no logs for this user"). */
    processLogs: (userId: string, date: string, processId?: string) =>
      client.get<ProcessLogRow[]>(
        processId
          ? '/v3/admin/bot/log/processes/{userId}/{date}/{processId}'
          : '/v3/admin/bot/log/processes/{userId}/{date}',
        { params: processId ? { userId, date, processId } : { userId, date }, cache: false },
      ),

    // --- commission / usage fee / wallet (#29-31) --------------------------------
    commissionMonth: (email: string, month?: string | number) =>
      client.get<Commission>('/v3/admin/spot/statistics/commission/month', { query: { email, month }, cache: false }),
    usageFeeMonth: (email: string, month?: string | number) =>
      client.get<UsageFee>('/v3/admin/spot/statistics/usagefee/month', { query: { email, month }, cache: false }),
    /** #31 — USDT only (predates the USDC fallback the user-facing wallet
     * view has). */
    wallet: (exchangeId: string, email: string) =>
      client.get<WalletRow[]>('/v3/admin/spot/bot/{exchangeId}/wallet', { params: { exchangeId }, query: { email }, cache: false }),
  }
}

export type AdminClient = ReturnType<typeof createAdminClient>
